import {
	ChangeDetectionStrategy,
	ChangeDetectorRef,
	Component,
	inject,
	OnInit,
	signal,
} from '@angular/core';
import {
	FormControl,
	FormGroup,
	FormsModule,
	ReactiveFormsModule,
} from '@angular/forms';
import {
	DestroyService,
	DialogBase,
	DialogOptions,
} from '@context/frontend/common';
import { getUaDevice } from '@context/frontend/native/platform';
import {
	PaginatedObject,
	PaginatedPageItem,
	PaginationPage,
} from '@context/frontend/pagination';
import { TableComponent } from '@context/frontend/table';
import { AuthService } from '@context/frontend/user';
import { Content } from '@context/shared/types/common';
import { hasInheritedRolePermission } from '@context/shared/types/user';
import { TranslocoModule } from '@jsverse/transloco';
import { TuiAutoFocus } from '@taiga-ui/cdk';
import {
	TuiButton,
	TuiLoader,
	TuiInput,
	TuiCheckbox,
	TuiRadio,
} from '@taiga-ui/core';
import { TuiButtonLoading } from '@taiga-ui/kit';
import { DocumentReference, where } from 'firebase/firestore';
import { debounceTime, takeUntil, tap } from 'rxjs';
import { ContentRowComponent } from '../../components';
import { ContentService } from '../../services';

type SelectContentValue =
	PaginatedPageItem<Content>[] | PaginatedPageItem<Content> | null;
@Component({
	imports: [
		TuiLoader,
		TuiButton,
		TuiButtonLoading,
		TranslocoModule,
		TableComponent,
		TuiInput,
		FormsModule,
		ReactiveFormsModule,
		ContentRowComponent,
		TuiCheckbox,
		TuiAutoFocus,
		TuiRadio,
	],
	selector: 'ctx-select-content-dialog',
	templateUrl: 'select-content-dialog.component.html',
	styleUrl: 'select-content-dialog.component.scss',
	changeDetection: ChangeDetectionStrategy.OnPush,
	providers: [DestroyService],
	host: {
		'[attr.device]': 'device',
	},
})
export class SelectContentDialogComponent
	extends DialogBase<
		SelectContentValue,
		{
			type: 'file' | 'folder' | 'all';
			multiSelect?: boolean;
			initialSelection?: DocumentReference<Content>[];
		}
	>
	implements OnInit
{
	static override readonly DialogOptions = {
		label: 'content.select-content-label',
		size: 'm',
	} as DialogOptions;

	readonly device = getUaDevice();

	private readonly contentService = inject(ContentService);
	private readonly cdRef = inject(ChangeDetectorRef);
	private readonly destroy$ = inject(DestroyService);
	private readonly authService = inject(AuthService);

	form: FormGroup | null = null;

	readonly searching = signal(false);
	readonly loading = signal(false);
	readonly busy = signal(false);

	readonly placeholder = {
		icon: '@tui.search',
		header: 'data.search-placeholder-header',
		content: 'data.search-placeholder-content',
	};

	readonly placeholderContent = {
		icon: '@tui.folder',
		header: 'content.table-placeholder-header',
		content: 'content.table-placeholder-select-content',
	};

	content = new PaginatedObject<Content>();

	readonly filteredContent = signal<PaginatedPageItem<Content>[]>([]);

	readonly multiSelect = signal(false);

	ngOnInit() {
		this.setupForm();
	}

	async setupForm() {
		this.loading.set(true);

		const multiSelect = this.context.data.multiSelect ?? false;
		this.multiSelect.set(multiSelect);

		const constraints = [where('archivedAt', '==', null)];

		const type = this.context.data.type ?? 'all';
		if (type !== 'all') constraints.push(where('type', '==', type));

		const user = this.authService.user;
		if (user && !hasInheritedRolePermission(user.role, 'admin')) {
			constraints.push(
				where('shared', 'array-contains', this.authService.userRef),
			);
		}

		this.content = new PaginatedObject({
			sortBy: 'name',
			sortDir: 'asc',
			pageSize: 100,
			constraints,
		});

		try {
			await this.onLoadContent();
		} catch (error) {
			console.error('SelectContentDialog: failed to load content', error);
			this.loading.set(false);
			this.cdRef.detectChanges();
			return;
		}

		// Get initial selection IDs if provided
		const initialSelection = this.context.data.initialSelection ?? [];
		const initialSelectionIds = new Set(
			initialSelection.map((ref) => ref.id),
		);

		if (multiSelect) {
			// For multi-select, use FormGroup with individual controls for each item
			const selectedGroup = new FormGroup({});
			this.content.items.forEach((item) => {
				const isSelected = initialSelectionIds.has(item.id);
				selectedGroup.addControl(item.id, new FormControl(isSelected));
			});
			this.form = new FormGroup({
				search: new FormControl(),
				selected: selectedGroup,
			});
		} else {
			// For single-select, use a single FormControl for radio buttons
			// Use the first initial selection if provided
			const initialSelectedId =
				initialSelection.length > 0 ? initialSelection[0].id : null;
			this.form = new FormGroup({
				search: new FormControl(),
				selected: new FormControl(initialSelectedId),
			});
		}

		this.loading.set(false);
		this.cdRef.detectChanges();

		this.form
			.get('search')
			?.valueChanges.pipe(
				tap(() => {
					this.searching.set(true);
				}),
				takeUntil(this.destroy$),
				debounceTime(250),
			)
			.subscribe((term) => {
				this.searching.set(false);
				this.onSetFiltered(term);
			});
	}

	onLoadContent = () =>
		this.contentService.fetch(this.content).then((value) => {
			this.content.update(value);

			// Update form controls if new items are added (only for multi-select)
			if (this.multiSelect() && this.form) {
				const selectedGroup = this.form.get('selected') as FormGroup;
				if (selectedGroup) {
					// Get initial selection IDs if provided
					const initialSelection =
						this.context.data.initialSelection ?? [];
					const initialSelectionIds = new Set(
						initialSelection.map((ref) => ref.id),
					);

					value.items.forEach((item) => {
						if (!selectedGroup.get(item.id)) {
							const isSelected = initialSelectionIds.has(item.id);
							selectedGroup.addControl(
								item.id,
								new FormControl(isSelected),
							);
						}
					});
				}
			}

			const filtered = this.onFilterContent(
				this.form?.get('search')?.value,
				value.page,
			);
			this.filteredContent.set(filtered);
		});

	onSetFiltered(term: string) {
		const filtered = this.onFilterContent(term, this.content.page);
		this.filteredContent.set(filtered);
	}

	onFilterContent = (
		term: string | null,
		page: PaginationPage<Content> | null,
	): PaginatedPageItem<Content>[] =>
		(page?.items ?? []).reduce((value, current) => {
			term = term?.trim().toLowerCase() ?? '';
			const content = current.data;

			if (
				!term ||
				content.name.toLowerCase().includes(term) ||
				content.description?.toLowerCase().includes(term)
			) {
				value.push(current);
			}

			return value;
		}, [] as PaginatedPageItem<Content>[]);

	onContentRemoved(contentRef: DocumentReference<Content>) {
		const items = this.content.items.filter((i) => i.id !== contentRef.id);
		const page = { items };
		this.content.update({ pages: { 0: page } });
		this.onSetFiltered(this.form?.get('search')?.value ?? '');
	}

	getSelected() {
		if (!this.form) return new Map<string, DocumentReference<Content>>();
		const selectedValue = this.form.get('selected')?.getRawValue();

		if (this.multiSelect()) {
			// For multi-select, selectedValue is a FormGroup object
			return this.content.items.reduce((value, current) => {
				if (selectedValue?.[current.id] === true)
					value.set(current.id, current.ref);
				return value;
			}, new Map<string, DocumentReference<Content>>());
		} else {
			// For single-select, selectedValue is a string (item ID)
			const map = new Map<string, DocumentReference<Content>>();
			if (selectedValue) {
				const item = this.content.items.find(
					(i) => i.id === selectedValue,
				);
				if (item) {
					map.set(item.id, item.ref);
				}
			}
			return map;
		}
	}

	onSubmit() {
		this.form?.markAllAsTouched();
		if (this.busy() || !this.form) return;

		const value = this.form.getRawValue();

		let returnValue: SelectContentValue = null;

		if (this.multiSelect()) {
			// Get selected items as an array from FormGroup
			returnValue = this.content.items.filter(
				(item) => value.selected?.[item.id] === true,
			);
		} else {
			// Get single selected item from FormControl
			const selectedId = value.selected;
			if (selectedId) {
				returnValue =
					this.content.items.find((item) => item.id === selectedId) ??
					null;
			}
		}

		this.busy.set(true);
		this.form.disable();
		this.onDismiss(returnValue);
	}
}
